import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNotification } from '../contexts/NotificationContext';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Plus,
  ChevronRight,
  ChevronDown,
  Edit2,
  Trash2,
  GripVertical,
  Folder,
  FolderOpen,
  Tag,
  X,
  Check,
  AlertCircle,
  Palette,
  Copy,
  Download,
  Upload,
  Store,
} from 'lucide-react';
import api, { storeApi } from '../lib/api';
import { useAuthStore } from '../store/auth';
import { EmptyState } from '../components/EmptyState';
import { PageHeader } from '../components/ui/PageHeader';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { FormField } from '../components/ui/FormField';
import { useConfirmDialog } from '../contexts/ConfirmDialogContext';

interface StoreBasic {
  id: string;
  name: string;
}

interface StatusGroup {
  id: string;
  name: string;
  color: string;
  icon?: string;
  parentId: string | null;
  storeId: string | null;
  position: number;
  isActive: boolean;
  children: StatusGroup[];
  store?: {
    id: string;
    name: string;
  };
  _count?: {
    orders: number;
  };
  settings?: {
    customTextColor?: string;
    /** Alternatywny kolor statusu używany tylko w light mode (opcjonalny). */
    lightColor?: string;
    /** Alternatywny kolor tekstu dla light mode (opcjonalny, używany z lightColor). */
    customTextColorLight?: string;
  };
}

const predefinedColors = [
  '#00f5ff', // cyan
  '#bf00ff', // purple
  '#ff00f5', // pink
  '#0066ff', // blue
  '#00ff66', // green
  '#ffaa00', // orange
  '#ff4444', // red
  '#888888', // gray
];

export function StatusManagementPage() {
  const userId = useAuthStore(state => state.user?.id);
  const tenantId = useAuthStore(state => state.tenant?.id);
  const sessionKey = `ecomflow_status_expansion:${tenantId}:${userId}`;
  return <StatusManagementContent key={sessionKey} sessionKey={sessionKey} />;
}

function StatusManagementContent({ sessionKey }: { sessionKey: string }) {
  const { showSuccess, showError } = useNotification();
  const confirmDialog = useConfirmDialog();
  const [statuses, setStatuses] = useState<StatusGroup[]>([]);
  const [flatStatuses, setFlatStatuses] = useState<StatusGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(() => {
    try {
      const saved: unknown = JSON.parse(sessionStorage.getItem(sessionKey) || '[]');
      return new Set(Array.isArray(saved) ? saved.filter((id): id is string => typeof id === 'string') : []);
    } catch {
      return new Set();
    }
  });

  useEffect(() => {
    try {
      sessionStorage.setItem(sessionKey, JSON.stringify([...expandedGroups]));
    } catch {
      // Keep in-memory expansion working when browser storage is unavailable.
    }
  }, [sessionKey, expandedGroups]);
  const [editingStatus, setEditingStatus] = useState<StatusGroup | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addingToParent, setAddingToParent] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    loadStatuses();
  }, []);

  const loadStatuses = async () => {
    try {
      setLoading(true);
      const { data } = await api.get<StatusGroup[]>('/statuses');
      setFlatStatuses(data);
      setStatuses(buildTree(data));
    } catch (err) {
      setError('Nie udało się załadować statusów');
    } finally {
      setLoading(false);
    }
  };

  const buildTree = (items: StatusGroup[]): StatusGroup[] => {
    const map = new Map<string, StatusGroup>();
    const roots: StatusGroup[] = [];

    items.forEach(item => {
      map.set(item.id, { ...item, children: [] });
    });

    items.forEach(item => {
      const node = map.get(item.id)!;
      if (item.parentId && map.has(item.parentId)) {
        map.get(item.parentId)!.children.push(node);
      } else {
        roots.push(node);
      }
    });

    // Sort by position
    const sortByPosition = (a: StatusGroup, b: StatusGroup) => a.position - b.position;
    roots.sort(sortByPosition);
    roots.forEach(root => {
      root.children.sort(sortByPosition);
      root.children.forEach(child => child.children.sort(sortByPosition));
    });

    return roots;
  };

  const toggleExpand = (id: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleDelete = async (id: string) => {
    if (!(await confirmDialog({
      title: 'Usuń status',
      message: 'Czy na pewno chcesz usunąć ten status? Wszystkie podstatusy również zostaną usunięte.',
      confirmLabel: 'Usuń',
    }))) {
      return;
    }
    try {
      await api.delete(`/statuses/${id}`);
      loadStatuses();
    } catch (err) {
      showError(err, 'status');
    }
  };

  const handleDuplicate = async (id: string) => {
    try {
      const { data } = await api.post<{ success: boolean; message: string }>(`/statuses/${id}/duplicate`);
      loadStatuses();
      showSuccess('save', { message: data.message });
    } catch (err) {
      showError(err, 'status');
    }
  };

  const handleExport = async () => {
    try {
      const { data } = await api.get<{
        version: string;
        exportedAt: string;
        statuses: Array<{
          name: string;
          color: string;
          icon: string | null;
          position: number;
          isActive: boolean;
          parentPath: string[];
        }>;
      }>('/statuses/export/all');
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `statusy-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      showError(err, 'export');
    }
  };

  const handleImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setImporting(true);
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      const { data: result } = await api.post<{ success: boolean; message: string; createdCount: number }>('/statuses/import', data);
      loadStatuses();
      showSuccess('save', { message: result.message });
    } catch (err) {
      showError(err, 'import');
    } finally {
      setImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleAddClick = (parentId: string | null = null) => {
    setAddingToParent(parentId);
    setShowAddModal(true);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const activeItem = flatStatuses.find(s => s.id === active.id);
    const overItem = flatStatuses.find(s => s.id === over.id);

    if (!activeItem || !overItem) return;

    // Only allow reordering within same parent
    if (activeItem.parentId !== overItem.parentId) return;

    // Get siblings
    const siblings = flatStatuses.filter(s => s.parentId === activeItem.parentId);
    const oldIndex = siblings.findIndex(s => s.id === active.id);
    const newIndex = siblings.findIndex(s => s.id === over.id);

    const reordered = arrayMove(siblings, oldIndex, newIndex);

    // Update positions
    const updates = reordered.map((item, index) => ({
      id: item.id,
      position: index,
    }));

    try {
      await api.post('/statuses/reorder', { items: updates });
      loadStatuses();
    } catch (err) {
      console.error('Failed to reorder:', err);
    }
  };

  // Get items for current level (roots)
  const rootItems = statuses.map(s => s.id);

  return (
    <div className="space-y-8">
      <div className="animate-fade-in">
        <PageHeader
          icon={Tag}
          title="Statusy zamówień"
          subtitle="Zarządzaj grupami i statusami dla organizacji zamówień"
          actions={
            <>
              <Button
                variant="secondary"
                onClick={handleExport}
                title="Eksportuj wszystkie statusy do pliku JSON"
                leftIcon={<Download />}
              >
                Eksport
              </Button>
              <label
                className="inline-flex items-center gap-2 px-field-x py-field-y rounded-btn text-sm font-semibold bg-surface-hover/60 text-text-primary border border-accent-primary/30 hover:border-accent-primary/50 hover:bg-surface-muted/60 backdrop-blur-sm cursor-pointer transition-colors"
                title="Importuj statusy z pliku JSON"
              >
                <span className="inline-flex items-center justify-center w-4 h-4 [&>svg]:w-full [&>svg]:h-full">
                  <Upload />
                </span>
                {importing ? 'Importowanie...' : 'Import'}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".json"
                  onChange={handleImport}
                  className="hidden"
                  disabled={importing}
                />
              </label>
              <Button
                variant="primary"
                onClick={() => handleAddClick(null)}
                leftIcon={<Plus />}
              >
                Dodaj grupę główną
              </Button>
            </>
          }
        />
      </div>

      {error && (
        <div className="flex items-center p-4 bg-danger/10 border border-danger/30 rounded-xl text-danger animate-scale-in">
          <AlertCircle className="w-5 h-5 mr-3 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Info about "All" tab */}
      <div className="card bg-gradient-to-r from-accent-primary/10 to-accent-secondary/10 border-accent-primary/20 animate-slide-up">
        <div className="flex items-start gap-4">
          <div className="p-3 rounded-xl bg-accent-primary/20">
            <Folder className="w-6 h-6 text-accent-primary" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-text-primary">Zakładka "Wszystkie"</h3>
            <p className="text-text-secondary mt-1">
              Zakładka "Wszystkie" jest zawsze widoczna na górze listy zamówień i agreguje wszystkie
              zamówienia ze wszystkich sklepów. Jest ona niezmienna i nie można jej modyfikować.
            </p>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="loader" />
        </div>
      ) : statuses.length === 0 ? (
        <div className="animate-slide-up">
          <EmptyState
            icon={Tag}
            title="Brak statusów"
            description="Utwórz pierwszą grupę statusów, np. nazwę sklepu, a potem dodaj do niej podgrupy."
            buttonLabel="Dodaj pierwszą grupę"
            onButtonClick={() => handleAddClick(null)}
            buttonIcon={Plus}
          />
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={rootItems} strategy={verticalListSortingStrategy}>
            <div className="space-y-3 animate-slide-up">
              {statuses.map((status) => (
                <SortableStatusItem
                  key={status.id}
                  status={status}
                  level={0}
                  expandedGroups={expandedGroups}
                  onToggleExpand={toggleExpand}
                  onEdit={setEditingStatus}
                  onDelete={handleDelete}
                  onDuplicate={handleDuplicate}
                  onAddChild={handleAddClick}
                  onDragEnd={handleDragEnd}
                  sensors={sensors}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {/* Add/Edit Modal */}
      {(showAddModal || editingStatus) && (
        <StatusModal
          status={editingStatus}
          parentId={addingToParent}
          onClose={() => {
            setShowAddModal(false);
            setEditingStatus(null);
            setAddingToParent(null);
          }}
          onSuccess={() => {
            if (addingToParent) {
              setExpandedGroups(previous => new Set([...previous, addingToParent]));
            }
            setShowAddModal(false);
            setEditingStatus(null);
            setAddingToParent(null);
            loadStatuses();
          }}
        />
      )}
    </div>
  );
}

function SortableStatusItem({
  status,
  level,
  expandedGroups,
  onToggleExpand,
  onEdit,
  onDelete,
  onDuplicate,
  onAddChild,
  onDragEnd,
  sensors,
}: {
  status: StatusGroup;
  level: number;
  expandedGroups: Set<string>;
  onToggleExpand: (id: string) => void;
  onEdit: (status: StatusGroup) => void;
  onDelete: (id: string) => void;
  onDuplicate: (id: string) => void;
  onAddChild: (parentId: string) => void;
  onDragEnd: (event: DragEndEvent) => void;
  sensors: ReturnType<typeof useSensors>;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: status.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    marginLeft: `${level * 24}px`,
  };

  const isExpanded = expandedGroups.has(status.id);
  const hasChildren = status.children.length > 0;
  const canAddChildren = level < 3; // Max 4 levels (0, 1, 2, 3)

  const childIds = status.children.map(c => c.id);

  return (
    <div ref={setNodeRef} style={style}>
      <div className="card !p-3 flex items-center gap-3 group hover:border-border">
        {/* Drag Handle */}
        <button
          {...attributes}
          {...listeners}
          className="p-1 rounded cursor-grab active:cursor-grabbing hover:bg-surface-hover"
        >
          <GripVertical className="w-5 h-5 text-text-disabled" />
        </button>

        {/* Expand/Collapse */}
        <button
          onClick={() => hasChildren && onToggleExpand(status.id)}
          className={`p-1 rounded ${hasChildren ? 'hover:bg-surface-hover' : ''}`}
          disabled={!hasChildren}
        >
          {hasChildren ? (
            isExpanded ? (
              <ChevronDown className="w-5 h-5 text-text-secondary" />
            ) : (
              <ChevronRight className="w-5 h-5 text-text-secondary" />
            )
          ) : (
            <div className="w-5 h-5" />
          )}
        </button>

        {/* Color indicator */}
        <div
          className="w-4 h-4 rounded-full flex-shrink-0"
          style={{ backgroundColor: status.color }}
        />

        {/* Icon */}
        {hasChildren ? (
          isExpanded ? (
            <FolderOpen className="w-5 h-5 text-text-secondary" />
          ) : (
            <Folder className="w-5 h-5 text-text-secondary" />
          )
        ) : (
          <Tag className="w-5 h-5 text-text-secondary" />
        )}

        {/* Name */}
        <span className="flex-1 text-text-primary font-medium">{status.name}</span>

        {/* Order count */}
        {status._count && status._count.orders > 0 && (
          <span className="text-sm text-text-muted">{status._count.orders} zamówień</span>
        )}

        {/* Actions */}
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          {canAddChildren && (
            <button
              onClick={() => onAddChild(status.id)}
              className="p-2 rounded-lg text-text-secondary hover:text-success hover:bg-success/10 transition-all"
              title="Dodaj podgrupę"
            >
              <Plus className="w-4 h-4" />
            </button>
          )}
          {level === 0 && (
            <button
              onClick={() => onDuplicate(status.id)}
              className="p-2 rounded-lg text-text-secondary hover:text-accent-secondary hover:bg-accent-secondary/10 transition-all"
              title="Kopiuj grupę z podstatusami"
            >
              <Copy className="w-4 h-4" />
            </button>
          )}
          <button
            onClick={() => onEdit(status)}
            className="p-2 rounded-lg text-text-secondary hover:text-accent-primary hover:bg-accent-primary/10 transition-all"
            title="Edytuj"
          >
            <Edit2 className="w-4 h-4" />
          </button>
          <button
            onClick={() => onDelete(status.id)}
            className="p-2 rounded-lg text-text-secondary hover:text-danger hover:bg-danger/10 transition-all"
            title="Usuń"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Children */}
      {isExpanded && hasChildren && (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={onDragEnd}
        >
          <SortableContext items={childIds} strategy={verticalListSortingStrategy}>
            <div className="mt-2 space-y-2">
              {status.children.map((child) => (
                <SortableStatusItem
                  key={child.id}
                  status={child}
                  level={level + 1}
                  expandedGroups={expandedGroups}
                  onToggleExpand={onToggleExpand}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  onDuplicate={onDuplicate}
                  onAddChild={onAddChild}
                  onDragEnd={onDragEnd}
                  sensors={sensors}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
    </div>
  );
}

function StatusModal({
  status,
  parentId,
  onClose,
  onSuccess,
}: {
  status: StatusGroup | null;
  parentId: string | null;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [name, setName] = useState(status?.name || '');
  const [color, setColor] = useState(status?.color || '#00f5ff');
  const [storeId, setStoreId] = useState<string>(status?.storeId || '');
  const [useCustomTextColor, setUseCustomTextColor] = useState(!!status?.settings?.customTextColor);
  const [customTextColor, setCustomTextColor] = useState(status?.settings?.customTextColor || '#ffffff');
  const [useLightColor, setUseLightColor] = useState(!!status?.settings?.lightColor);
  const [lightColor, setLightColor] = useState(status?.settings?.lightColor || status?.color || '#2563eb');
  const [useLightTextColor, setUseLightTextColor] = useState(!!status?.settings?.customTextColorLight);
  const [lightTextColor, setLightTextColor] = useState(status?.settings?.customTextColorLight || '#111827');
  const [stores, setStores] = useState<StoreBasic[]>([]);
  const [loadingStores, setLoadingStores] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const isEditing = !!status;
  // Pokazuj wybór sklepu tylko dla statusów głównych (nie mających rodzica)
  const showStoreSelect = !parentId && (!isEditing || status?.parentId === null);

  // Załaduj sklepy przy montowaniu
  useEffect(() => {
    if (showStoreSelect) {
      setLoadingStores(true);
      storeApi.getAll()
        .then(({ data }) => setStores(data))
        .catch(console.error)
        .finally(() => setLoadingStores(false));
    }
  }, [showStoreSelect]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const settings: {
      customTextColor?: string;
      lightColor?: string;
      customTextColorLight?: string;
    } = {};
    if (useCustomTextColor) settings.customTextColor = customTextColor;
    if (useLightColor) {
      settings.lightColor = lightColor;
      if (useLightTextColor) settings.customTextColorLight = lightTextColor;
    }

    try {
      if (isEditing) {
        await api.patch(`/statuses/${status.id}`, {
          name,
          color,
          settings,
          ...(showStoreSelect && { storeId: storeId || null }),
        });
      } else {
        await api.post('/statuses', {
          name,
          color,
          settings,
          parentId,
          ...(showStoreSelect && { storeId: storeId || null }),
        });
      }
      onSuccess();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } };
      setError(error.response?.data?.error || 'Wystąpił błąd');
    } finally {
      setLoading(false);
    }
  };

  // Portal do document.body — uniezależnia modal od layoutu parent (space-y-8
   // w StatusManagementPage powodowało przesunięcie top przez margin-top).
  return createPortal(
    <div className="modal-backdrop flex items-center justify-center animate-fade-in">
      <div className="modal max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-text-primary">
            {isEditing ? 'Edytuj status' : parentId ? 'Dodaj podgrupę' : 'Dodaj grupę główną'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-text-secondary hover:text-text-primary hover:bg-surface-hover transition-all"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          {error && (
            <div className="flex items-center p-4 bg-danger/10 border border-danger/30 rounded-xl text-danger">
              <AlertCircle className="w-5 h-5 mr-3 flex-shrink-0" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          <FormField
            label={
              <span className="inline-flex items-center gap-2">
                <Tag className="w-4 h-4" />
                Nazwa
              </span>
            }
            required
          >
            <Input
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={parentId ? 'np. Nowe, Do realizacji...' : 'np. Sklep Online, Butik Fashion...'}
            />
          </FormField>

          {/* Wybór sklepu - tylko dla statusów głównych */}
          {showStoreSelect && (
            <FormField
              label={
                <span className="inline-flex items-center gap-2">
                  <Store className="w-4 h-4" />
                  Powiązany sklep (opcjonalnie)
                </span>
              }
              helper="Statusy powiązane ze sklepem będą widoczne tylko dla zamówień z tego sklepu."
            >
              <Select
                value={storeId}
                onChange={(e) => setStoreId(e.target.value)}
                disabled={loadingStores}
              >
                <option value="">Wszystkie sklepy (globalny)</option>
                {stores.map(store => (
                  <option key={store.id} value={store.id}>{store.name}</option>
                ))}
              </Select>
            </FormField>
          )}

          <div>
            <label className="label flex items-center gap-2">
              <Palette className="w-4 h-4" />
              Kolor
            </label>
            <div className="flex items-center gap-3">
              <div className="flex gap-2 flex-wrap">
                {predefinedColors.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setColor(c)}
                    className={`w-8 h-8 rounded-lg transition-all ${
                      color === c ? 'ring-2 ring-white ring-offset-2 ring-offset-surface-raised' : ''
                    }`}
                    style={{ backgroundColor: c }}
                  />
                ))}
              </div>
              <input
                type="color"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                className="w-8 h-8 rounded cursor-pointer bg-transparent"
              />
            </div>
          </div>

          {/* Custom text color option */}
          <div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={useCustomTextColor}
                onChange={(e) => setUseCustomTextColor(e.target.checked)}
                className="w-4 h-4 rounded border-border bg-surface-hover text-accent-primary focus:ring-accent-primary/50"
              />
              <span className="text-sm text-text-secondary">Własny kolor tekstu</span>
            </label>
            {useCustomTextColor && (
              <div className="mt-3 flex items-center gap-3">
                <div className="flex gap-2 flex-wrap">
                  {['#ffffff', '#000000', '#e5e7eb', '#374151'].map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setCustomTextColor(c)}
                      className={`w-8 h-8 rounded-lg transition-all border border-border-strong ${
                        customTextColor === c ? 'ring-2 ring-white ring-offset-2 ring-offset-surface-raised' : ''
                      }`}
                      style={{ backgroundColor: c }}
                    />
                  ))}
                </div>
                <input
                  type="color"
                  value={customTextColor}
                  onChange={(e) => setCustomTextColor(e.target.value)}
                  className="w-8 h-8 rounded cursor-pointer bg-transparent"
                />
              </div>
            )}
          </div>

          {/* Alternatywny kolor dla light mode */}
          <div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={useLightColor}
                onChange={(e) => setUseLightColor(e.target.checked)}
                className="w-4 h-4 rounded border-border bg-surface-hover text-accent-primary focus:ring-accent-primary/50"
              />
              <span className="text-sm text-text-secondary">Alternatywny kolor dla jasnego motywu</span>
            </label>
            {useLightColor && (
              <div className="mt-3 space-y-3">
                <p className="text-xs text-text-muted">
                  Jasny motyw wyświetli ten kolor zamiast głównego. Przydatne gdy oryginalny kolor jest nieczytelny na białym tle.
                </p>
                <div className="flex items-center gap-3">
                  <div className="flex gap-2 flex-wrap">
                    {predefinedColors.map((c) => (
                      <button
                        key={c}
                        type="button"
                        onClick={() => setLightColor(c)}
                        className={`w-8 h-8 rounded-lg transition-all ${
                          lightColor === c ? 'ring-2 ring-accent-primary ring-offset-2 ring-offset-surface-raised' : ''
                        }`}
                        style={{ backgroundColor: c }}
                      />
                    ))}
                  </div>
                  <input
                    type="color"
                    value={lightColor}
                    onChange={(e) => setLightColor(e.target.value)}
                    className="w-8 h-8 rounded cursor-pointer bg-transparent"
                  />
                </div>

                {/* Własny kolor tekstu dla light mode (dostępne tylko gdy lightColor włączone) */}
                <label className="flex items-center gap-2 cursor-pointer pt-2 border-t border-border-subtle">
                  <input
                    type="checkbox"
                    checked={useLightTextColor}
                    onChange={(e) => setUseLightTextColor(e.target.checked)}
                    className="w-4 h-4 rounded border-border bg-surface-hover text-accent-primary focus:ring-accent-primary/50"
                  />
                  <span className="text-sm text-text-secondary">Własny kolor tekstu dla jasnego motywu</span>
                </label>
                {useLightTextColor && (
                  <div className="flex items-center gap-3">
                    <div className="flex gap-2 flex-wrap">
                      {['#ffffff', '#000000', '#111827', '#374151'].map((c) => (
                        <button
                          key={c}
                          type="button"
                          onClick={() => setLightTextColor(c)}
                          className={`w-8 h-8 rounded-lg transition-all border border-border-strong ${
                            lightTextColor === c ? 'ring-2 ring-accent-primary ring-offset-2 ring-offset-surface-raised' : ''
                          }`}
                          style={{ backgroundColor: c }}
                        />
                      ))}
                    </div>
                    <input
                      type="color"
                      value={lightTextColor}
                      onChange={(e) => setLightTextColor(e.target.value)}
                      className="w-8 h-8 rounded cursor-pointer bg-transparent"
                    />
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Preview — dwa warianty gdy lightColor włączony. Każdy na swoim
             tle (ciemne / białe) żeby pokazać jak status wygląda w docelowym
             motywie — nie w aktualnym. */}
          <div className="space-y-3">
            <div className="p-4 rounded-xl border border-border-subtle" style={{ background: '#1e1e26' }}>
              <p className="text-sm mb-2" style={{ color: '#d1d5db' }}>Podgląd (ciemny motyw):</p>
              <span
                className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap border"
                style={{
                  backgroundColor: `${color}20`,
                  color: useCustomTextColor ? customTextColor : color,
                  borderColor: 'rgba(255, 255, 255, 0.15)',
                }}
              >
                <span
                  className="w-1.5 h-1.5 rounded-full"
                  style={{ backgroundColor: color }}
                />
                {name || 'Nazwa statusu'}
              </span>
            </div>
            {useLightColor && (
              <div className="p-4 rounded-xl border border-border-subtle" style={{ background: '#ffffff' }}>
                <p className="text-sm mb-2" style={{ color: '#374151' }}>Podgląd (jasny motyw):</p>
                <span
                  className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap border"
                  style={{
                    backgroundColor: `${lightColor}20`,
                    color: useLightTextColor ? lightTextColor : (useCustomTextColor ? customTextColor : lightColor),
                    borderColor: 'rgba(10, 14, 30, 0.15)',
                  }}
                >
                  <span
                    className="w-1.5 h-1.5 rounded-full"
                    style={{ backgroundColor: lightColor }}
                  />
                  {name || 'Nazwa statusu'}
                </span>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 pt-2">
            <Button
              type="submit"
              variant="primary"
              disabled={loading}
              loading={loading}
              leftIcon={loading ? undefined : <Check />}
            >
              {loading ? 'Zapisywanie...' : isEditing ? 'Zapisz' : 'Dodaj'}
            </Button>
            <Button type="button" variant="secondary" onClick={onClose}>
              Anuluj
            </Button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
}
